import React, { useState } from 'react'
import NiceModal from '@ebay/nice-modal-react'
import { Button, Modal, Table } from '@arnsbogroup/react-voicearchive'
import { useIntl } from 'react-intl'
import SearchPage from 'containers/SearchPage'
import MemoryCreateModal from 'components/MemoryCreateModal'
import MemoryRequestActions from 'components/MemoryRequestActions'
import MemoryListBlock from 'components/MemoryListBlock'
import { MemoryStatusChip } from 'components/datatable'
import useCurrentUser from 'hooks/useCurrentUser'
import messages from './messages'

const MemoryBankPage = ({ location, history, match, ...props }) => {
  const { roles } = useCurrentUser()
  const { formatMessage } = useIntl()
  const isCoordinator = roles.includes('coordinator')

  const [selectedIds, setSelectedIds] = useState([])
  const [refreshKey, setRefreshKey] = useState(0)
  const [selectedMemory, setSelectedMemory] = useState(null)

  const refresh = () => {
    setSelectedIds([])
    setRefreshKey((k) => k + 1)
  }

  const deleteMemory = (memory) => {
    fetch(`/rest/v2/memory/${memory.uid}`, { method: 'DELETE' })
      .then((r) => r.json())
      .then(({ success, errors }) => {
        if (!success) {
          toastr.error((errors || []).join(', '))
          return
        }
        if (location.pathname === `/memory-bank/${memory.uid}`) {
          history.push('/memory-bank')
        }
        refresh()
      })
  }

  const handleRowClick = (memory) => {
    if (isCoordinator) {
      history.push(`/memory-bank/${memory.uid}`)
    } else {
      setSelectedMemory(memory)
    }
  }

  return (
    <>
      <div className={`${match.isExact ? 'w-full' : 'w-full max-w-md'} h-full overflow-auto p-4 space-y-4`}>
        {isCoordinator && match.isExact &&
          <div>
            <div className="flex justify-end">
              <Button
                icon={['fas', 'plus']}
                onClick={() => NiceModal.show(MemoryCreateModal, { onSave: refresh })}
              >
                {formatMessage(messages.createMemory)}
              </Button>
            </div>

            <MemoryRequestActions
              memoryIds={selectedIds}
              onSuccess={() => setSelectedIds([])}
            />
          </div>
        }

        <SearchPage
          key={refreshKey}
          {...props}
          match={match}
          location={location}
          history={history}
          baseApiUrl="/rest/v2/memory"
          containerClassName="w-full"
          rowSelectionKey={isCoordinator ? 'uid' : undefined}
          onRowSelected={isCoordinator ? (key) => setSelectedIds((prev) => prev.includes(key) ? prev.filter(id => id !== key) : [...prev, key]) : undefined}
          rowsSelected={isCoordinator ? selectedIds : undefined}
          rowActions={[
            {
              label: formatMessage(messages.downloadAudio),
              isVisible: (memory) => !!memory.audio_download_url,
              onClick: (memory) => window.open(memory.audio_download_url, '_blank'),
            },
            {
              label: formatMessage(messages.duplicate),
              isVisible: () => isCoordinator,
              onClick: async (memory) => {
                const { data } = await fetch(`/rest/v2/memory/${memory.uid}?includeRelations=1`).then(r => r.json())
                NiceModal.show(MemoryCreateModal, { initialMemory: data || memory, onSave: refresh })
              },
            },
            {
              state: 'negative',
              label: formatMessage(messages.delete),
              isVisible: () => isCoordinator,
              onClick: (memory) => deleteMemory(memory),
            },
          ]}
          components={{
            Tr: ({ childProps = {}, ...innerProps }) => (
              <Table.Tr
                {...innerProps}
                onClick={childProps.uid ? () => handleRowClick(childProps) : undefined}
                className={{
                  'cursor-pointer': !!childProps.uid,
                  'bg-blue-50': isCoordinator && !!(childProps.uid && location.pathname === `/memory-bank/${childProps.uid}`),
                }}
              />
            ),
          }}
          head={{
            text: { label: formatMessage(messages.columnText), className: 'w-1/2' },
            language_name: { label: formatMessage(messages.columnLanguage), className: 'w-1/6' },
            status: { label: formatMessage(messages.columnRecorded), className: 'w-1/6' },
            audio: { label: formatMessage(messages.columnAudio), className: 'w-1/6' },
          }}
          body={{
            text: (memory) => <span className="text-sm">{memory.text || '-'}</span>,
            language_name: (memory) =>
              memory.language_name ? <span className="text-sm">{memory.language_name}</span> : null,
            status: (memory) => <MemoryStatusChip memory={memory} />,
            audio: (memory) => memory.audio_stream_url ? (
              <audio
                style={{
                  width: "100%",
                  minWidth: '180px',
                  height: '25px',
                }}
                preload="none"
                src={memory?.audio_stream_url}
                controls
                controlsList="nodownload"
              />
            ) : null,
          }}
        />
      </div>

      <Modal
        show={!!selectedMemory}
        title={selectedMemory?.text || formatMessage(messages.modalDefaultTitle)}
        onClose={() => setSelectedMemory(null)}
        onOutsideClick={() => setSelectedMemory(null)}
      >
        {selectedMemory && <MemoryListBlock onRecord={refresh} memories={[selectedMemory]} />}
      </Modal>
    </>
  )
}

export default MemoryBankPage
